/**
 * Notification Service
 * 
 * Handles notifications for likes, comments, follows, and other user activities.
 */

import { ref, set, get, query, orderByChild, limitToLast, onValue, push, update } from "firebase/database";
import { db } from "../auth/firebase";

/**
 * Create a notification
 * @param {string} userId - User ID to notify
 * @param {Object} notification - Notification object
 * @returns {Promise<string>} Notification ID
 */
export const createNotification = async (userId, notification) => {
  try {
    const notificationsRef = ref(db, `users/${userId}/notifications`);
    const newNotificationRef = push(notificationsRef);
    
    await set(newNotificationRef, {
      ...notification,
      read: false,
      createdAt: new Date().toISOString(),
    });
    
    return newNotificationRef.key;
  } catch (error) {
    console.error("Error creating notification:", error);
    throw error;
  }
};

/**
 * Mark notification as read
 * @param {string} userId - User ID
 * @param {string} notificationId - Notification ID
 * @returns {Promise<void>}
 */
export const markNotificationAsRead = async (userId, notificationId) => {
  try {
    const notificationRef = ref(db, `users/${userId}/notifications/${notificationId}`);
    await update(notificationRef, {
      read: true,
      readAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error("Error marking notification as read:", error);
    throw error;
  }
};

/**
 * Mark all notifications as read
 * @param {string} userId - User ID
 * @returns {Promise<void>}
 */
export const markAllNotificationsAsRead = async (userId) => {
  try {
    const notificationsRef = ref(db, `users/${userId}/notifications`);
    const snapshot = await get(notificationsRef);
    
    if (!snapshot.exists()) return;
    
    const updates = {};
    snapshot.forEach((childSnapshot) => {
      if (!childSnapshot.val().read) {
        updates[`${childSnapshot.key}/read`] = true;
        updates[`${childSnapshot.key}/readAt`] = new Date().toISOString();
      }
    });
    
    if (Object.keys(updates).length > 0) {
      await update(notificationsRef, updates);
    }
  } catch (error) {
    console.error("Error marking all notifications as read:", error);
    throw error;
  }
};

/**
 * Get user notifications
 * @param {string} userId - User ID
 * @param {number} limit - Maximum number of notifications to fetch
 * @returns {Promise<Array>}
 */
export const getUserNotifications = async (userId, limit = 50) => {
  try {
    const notificationsRef = ref(db, `users/${userId}/notifications`);
    const notificationsQuery = query(
      notificationsRef,
      orderByChild("createdAt"),
      limitToLast(limit)
    );
    
    const snapshot = await get(notificationsQuery);
    
    if (!snapshot.exists()) {
      return [];
    }
    
    const notifications = [];
    snapshot.forEach((childSnapshot) => {
      notifications.push({
        id: childSnapshot.key,
        ...childSnapshot.val(),
      });
    });
    
    // Reverse to show newest first
    return notifications.reverse();
  } catch (error) {
    console.error("Error getting user notifications:", error);
    throw error;
  }
};

/**
 * Subscribe to user notifications
 * @param {string} userId - User ID
 * @param {Function} callback - Callback function to receive notifications
 * @param {number} limit - Maximum number of notifications
 * @returns {Function} Unsubscribe function
 */
export const subscribeToNotifications = (userId, callback, limit = 50) => {
  const notificationsRef = ref(db, `users/${userId}/notifications`);
  const notificationsQuery = query(
    notificationsRef,
    orderByChild("createdAt"),
    limitToLast(limit)
  );
  
  const unsubscribe = onValue(notificationsQuery, (snapshot) => {
    if (!snapshot.exists()) {
      callback([]);
      return;
    }
    
    const notifications = [];
    snapshot.forEach((childSnapshot) => {
      notifications.push({
        id: childSnapshot.key,
        ...childSnapshot.val(),
      });
    });
    
    // Reverse to show newest first
    callback(notifications.reverse());
  });
  
  return unsubscribe;
};

/**
 * Get unread notifications count
 * @param {string} userId - User ID
 * @returns {Promise<number>}
 */
export const getUnreadCount = async (userId) => {
  try {
    const notificationsRef = ref(db, `users/${userId}/notifications`);
    const snapshot = await get(notificationsRef);
    
    if (!snapshot.exists()) {
      return 0;
    }
    
    let unreadCount = 0;
    snapshot.forEach((childSnapshot) => {
      if (!childSnapshot.val().read) {
        unreadCount++;
      }
    });
    
    return unreadCount;
  } catch (error) {
    console.error("Error getting unread count:", error);
    throw error;
  }
};

/**
 * Subscribe to unread notifications count
 * @param {string} userId - User ID
 * @param {Function} callback - Callback function to receive count updates
 * @returns {Function} Unsubscribe function
 */
export const subscribeToUnreadCount = (userId, callback) => {
  const notificationsRef = ref(db, `users/${userId}/notifications`);
  
  const unsubscribe = onValue(notificationsRef, (snapshot) => {
    if (!snapshot.exists()) {
      callback(0);
      return;
    }
    
    let unreadCount = 0;
    snapshot.forEach((childSnapshot) => {
      if (!childSnapshot.val().read) {
        unreadCount++;
      }
    });
    
    callback(unreadCount);
  });
  
  return unsubscribe;
};

/**
 * Delete notification
 * @param {string} userId - User ID
 * @param {string} notificationId - Notification ID
 * @returns {Promise<void>}
 */
export const deleteNotification = async (userId, notificationId) => {
  try {
    const notificationRef = ref(db, `users/${userId}/notifications/${notificationId}`);
    await set(notificationRef, null);
  } catch (error) {
    console.error("Error deleting notification:", error);
    throw error;
  }
};

/**
 * Helper function to create notification for like
 */
export const notifyLike = async (contentOwnerId, contentId, contentType, likerName, likerId) => {
  if (!contentOwnerId || contentOwnerId === likerId) return;
  
  await createNotification(contentOwnerId, {
    type: "like",
    message: `${likerName} liked your work`,
    contentId,
    contentType,
    actorId: likerId,
    actorName: likerName,
  });
};

/**
 * Helper function to create notification for comment
 */
export const notifyComment = async (contentOwnerId, contentId, contentType, commenterName, commenterId) => {
  if (!contentOwnerId || contentOwnerId === commenterId) return;
  
  await createNotification(contentOwnerId, {
    type: "comment",
    message: `${commenterName} commented on your work`,
    contentId,
    contentType,
    actorId: commenterId,
    actorName: commenterName,
  });
};

/**
 * Helper function to create notification for follow
 */
export const notifyFollow = async (followedUserId, followerName, followerId) => {
  if (!followedUserId || followedUserId === followerId) return;
  
  await createNotification(followedUserId, {
    type: "follow",
    message: `${followerName} started following you`,
    actorId: followerId,
    actorName: followerName,
  });
};

/**
 * Notify followers when a writer adds new content.
 * Creates a "new_content" notification for each follower of the writer.
 * @param {string} writerId - Writer (author) user ID
 * @param {string} writerName - Writer display name
 * @param {string} contentType - legacy slug for notification payload / deep links
 * @param {string} contentId - New content ID
 * @param {string} contentTitle - Title of the new content
 */
export const notifyFollowersNewContent = async (
  writerId,
  writerName,
  contentType,
  contentId,
  contentTitle
) => {
  if (!writerId || !writerName || !contentType || !contentId) return;
  try {
    const { getFollowers } = await import("./followService");
    const followers = await getFollowers(writerId);
    if (!followers.length) return;
    const titlePart = contentTitle ? `: ${contentTitle}` : "";
    const message = `${writerName} published new work${titlePart}`;
    for (const f of followers) {
      const followerId = f.userId || f;
      if (followerId && followerId !== writerId) {
        await createNotification(followerId, {
          type: "new_content",
          message,
          contentId,
          contentType,
          actorId: writerId,
          actorName: writerName,
        });
      }
    }
  } catch (error) {
    console.error("Error notifying followers of new content:", error);
  }
};

